import React, { useState } from "react";
import { RenderMarkdownWithLatex } from "../../utils/utils"; 
import SolutionStepper from '../molecules/SolutionStepper';
import HintAndFormulas from '../molecules/HintAndFormulas';

const CorrectionModal = ({
  isOpen,
  onClose,
  originalProblem, 
  correctedData,   
  onAcceptCorrection,
  isAccepting,
  error,
}) => {
  // Lokalne stany do pokazywania/ukrywania sekcji (domyślnie otwarte, żeby było wygodnie)
  const [showSugHint, setShowSugHint] = useState(true);
  const [showOrigHint, setShowOrigHint] = useState(true);
  const [showSugSol, setShowSugSol] = useState(true);
  const [showOrigSol, setShowOrigSol] = useState(true);

  if (!isOpen || !correctedData || !originalProblem) return null;

  const correctedAnswers = [
    { text: correctedData.corrected_true_answer, isCorrect: true },
    { text: correctedData.corrected_false_answer1, isCorrect: false },
    { text: correctedData.corrected_false_answer2, isCorrect: false },
    { text: correctedData.corrected_false_answer3, isCorrect: false },
  ].filter(ans => ans && ans.text);

  const originalAnswers = [
    { text: originalProblem.true_answer, isCorrect: true },
    { text: originalProblem.false_answer1, isCorrect: false },
    { text: originalProblem.false_answer2, isCorrect: false },
    { text: originalProblem.false_answer3, isCorrect: false },
  ].filter(ans => ans && ans.text); 

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            Sugestia Poprawy Zadania (ID: {originalProblem.problem_id})
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
            aria-label="Zamknij"
          >
            &times;
          </button>
        </div>

        {correctedData.correction_summary && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-300 rounded-md text-sm text-yellow-800 shadow-sm">
            <h4 className="font-bold mb-2 uppercase tracking-wide text-xs">Podsumowanie zmian (AI):</h4>
            <RenderMarkdownWithLatex text={correctedData.correction_summary} />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"> 
          
          {/* --- KOLUMNA LEWA: SUGESTIA AI --- */}
          <div className="space-y-6 border-r pr-4 border-dashed border-gray-200">
             <h3 className="text-lg font-bold text-blue-700 uppercase tracking-wider mb-4 border-b border-blue-100 pb-2">
                🌟 Wersja Poprawiona (AI)
             </h3>

             {/* Treść */}
             <section>
                <div className="text-xs font-bold text-gray-500 uppercase mb-1">Treść zadania</div>
                <div className="p-3 border border-blue-200 rounded bg-blue-50 text-gray-900 text-sm leading-relaxed">
                  <RenderMarkdownWithLatex text={correctedData.corrected_question || "Brak treści."} />
                </div>
             </section>

             {/* Odpowiedzi */}
             <section>
                <div className="text-xs font-bold text-gray-500 uppercase mb-1">Odpowiedzi</div>
                <div className="space-y-2">
                  {correctedAnswers.map((ans, i) => (
                    <div key={i} className={`p-2 rounded border text-sm ${ans.isCorrect ? 'bg-green-100 border-green-300 text-green-900' : 'bg-white border-gray-200 text-gray-600'}`}>
                       <RenderMarkdownWithLatex text={ans.text} />
                    </div>
                  ))}
                </div>
             </section>

             {/* Podpowiedź (HintAndFormulas) */}
             {correctedData.corrected_hint && (
                <section>
                   <button 
                      onClick={() => setShowSugHint(!showSugHint)}
                      className="text-xs font-bold text-blue-600 uppercase mb-1 hover:underline flex items-center gap-1"
                   >
                      {showSugHint ? "▼ Podpowiedź" : "▶ Pokaż Podpowiedź"}
                   </button>
                   {showSugHint && (
                      <div className="border border-blue-100 p-1 rounded bg-blue-50/30">
                         <HintAndFormulas hintText={correctedData.corrected_hint} formulas={[]} />
                      </div>
                   )}
                </section>
             )}

             {/* Rozwiązanie (Stepper) */}
             {correctedData.corrected_solution && (
                <section>
                   <button 
                      onClick={() => setShowSugSol(!showSugSol)}
                      className="text-xs font-bold text-blue-600 uppercase mb-1 hover:underline flex items-center gap-1"
                   >
                      {showSugSol ? "▼ Rozwiązanie (Stepper)" : "▶ Pokaż Rozwiązanie"}
                   </button>
                   {showSugSol && (
                      <div className="border border-blue-100 p-1 rounded bg-blue-50/30">
                         <SolutionStepper solutionText={correctedData.corrected_solution} />
                      </div>
                   )}
                </section>
             )}
          </div>


          {/* --- KOLUMNA PRAWA: ORYGINAŁ --- */}
          <div className="space-y-6 pl-2">
             <h3 className="text-lg font-bold text-gray-500 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">
                📜 Wersja Oryginalna
             </h3>

             {/* Treść */}
             <section>
                <div className="text-xs font-bold text-gray-400 uppercase mb-1">Treść zadania</div>
                <div className="p-3 border border-gray-200 rounded bg-gray-50 text-gray-600 text-sm leading-relaxed grayscale opacity-80">
                  <RenderMarkdownWithLatex text={originalProblem.question || "Brak treści."} />
                </div>
             </section>

             {/* Odpowiedzi */}
             <section>
                <div className="text-xs font-bold text-gray-400 uppercase mb-1">Odpowiedzi</div>
                <div className="space-y-2 opacity-80">
                  {originalAnswers.map((ans, i) => (
                    <div key={i} className={`p-2 rounded border text-sm ${ans.isCorrect ? 'bg-gray-200 border-gray-300 text-gray-800' : 'bg-white border-gray-100 text-gray-400'}`}>
                       <RenderMarkdownWithLatex text={ans.text} />
                    </div>
                  ))}
                </div>
             </section>

             {/* Podpowiedź */}
             {originalProblem.hint && (
                <section>
                   <button 
                      onClick={() => setShowOrigHint(!showOrigHint)}
                      className="text-xs font-bold text-gray-400 uppercase mb-1 hover:text-gray-600 flex items-center gap-1"
                   >
                      {showOrigHint ? "▼ Podpowiedź" : "▶ Pokaż Podpowiedź"}
                   </button>
                   {showOrigHint && (
                      <div className="opacity-70 grayscale">
                         <HintAndFormulas hintText={originalProblem.hint} formulas={[]} />
                      </div>
                   )}
                </section>
             )}

             {/* Rozwiązanie */}
             {originalProblem.solution && (
                <section>
                   <button 
                      onClick={() => setShowOrigSol(!showOrigSol)}
                      className="text-xs font-bold text-gray-400 uppercase mb-1 hover:text-gray-600 flex items-center gap-1"
                   >
                      {showOrigSol ? "▼ Rozwiązanie" : "▶ Pokaż Rozwiązanie"}
                   </button>
                   {showOrigSol && (
                      <div className="opacity-70 grayscale">
                         <SolutionStepper solutionText={originalProblem.solution} />
                      </div>
                   )}
                </section>
             )}
          </div>

        </div>

        {error && <p className="text-red-600 text-sm mt-6 font-bold text-center bg-red-50 p-2 rounded">{error}</p>}

        <div className="mt-8 flex justify-end space-x-3 border-t pt-6 bg-gray-50 -mx-6 -mb-6 p-4 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={isAccepting}
            className="px-6 py-2.5 text-sm font-bold text-gray-600 bg-white hover:bg-gray-100 rounded-lg border border-gray-300 shadow-sm transition-all"
          >
            Anuluj
          </button>
          <button
            onClick={onAcceptCorrection}
            disabled={isAccepting}
            className="px-6 py-2.5 text-sm font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg shadow-md hover:shadow-lg transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isAccepting ? (
                <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Zapisywanie...
                </>
            ) : (
                "✅ Zaakceptuj i Zapisz"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CorrectionModal;